import { queryOptions } from '@tanstack/vue-query';
import type { AxiosError, AxiosResponse } from 'axios';
import type { MaybeRef } from 'vue';
import { toValue } from 'vue';

import { axios } from '@/helpers/axios';
import type { KeywordListData, TrendItem } from '@/modules/keyword-lists/types';

const keywordListNamesKey = ['keywordlist', 'names'];
const keywordListSearchVolumeKeyBase  = ['keywordlist', 'search-volume-trends'];

interface keywordType {
  keyword: string,
  volume: number | undefined | null,
  trend: 'up' | 'down' | 'flat' | undefined | null,
  search_trend: {
    date: string;
    value: number;
    percentage: number;
  }[],
}

export const useKeywordListNamesQueryOptions  = () => queryOptions<AxiosResponse, AxiosError, {
  value: number;
  label: string;
}[]>({
  queryKey: keywordListNamesKey,
  queryFn: () => axios.get('/keywordlist/names'),
  select: (response) => response.data.map((item) => ({
    value: item.id,
    label: item.name,
  })),
  staleTime: 0,
  gcTime: 0,
});

export const useKeywordListSearchVolumeQueryOptions = (selectedKeywordListId: MaybeRef<number | null>) => queryOptions<
  AxiosResponse<{ data: KeywordListData[]; meta: Record<string, unknown> }>,
  AxiosError,
  keywordType[]
>({
  queryKey: [...keywordListSearchVolumeKeyBase, selectedKeywordListId],
  queryFn: () => {
    const listId = toValue(selectedKeywordListId);
    if (listId == null) {
      throw new Error('A keyword list id is required to load keyword list terms.');
    }
    return axios.get(`/keywordlist/terms-kl/${listId}/etsy/USA/0`, {
      params: {
        page: 1,
        per_page: 5,
        sort_by: 'updated_dt',
        sort_dir: 'desc',
        include_stats: 1,
        include_google: 0,
        include_competition: 0,
      },
    });
  },
  enabled: () => toValue(selectedKeywordListId) !== null,
  select: (response) => {
    const records = response.data?.data ?? [];

    const deriveTrend = (trend: TrendItem[] | undefined): 'up' | 'down' | 'flat' => {
      if (!trend || trend.length === 0) return 'flat';
      const values = trend
        .map((t) => (typeof t.value === 'number' ? t.value : Number(t.value ?? 0)))
        .filter((v) => Number.isFinite(v));
      if (values.length < 2) return 'flat';
      const first = values[0];
      const last = values[values.length - 1];
      if (last > first) return 'up';
      if (last < first) return 'down';
      return 'flat';
    };

    return records.map((item) => ({
      keyword: item.keyword,
      volume: item?.avg_searches?.order_value ?? null,
      trend: deriveTrend(item.search_trend),
      search_trend: (item.search_trend ?? []).map((trend) => {
        const rawValue = typeof trend.value === 'number' ? trend.value : Number(trend.value ?? 0);
        return {
          percentage: trend.percentage,
          value: Number.isFinite(rawValue) ? rawValue : 0,
          date: trend.date.slice(0, 3),
        };
      }),
    }));
  },
  staleTime: 0,
  gcTime: 0,
});
